// Live trust data (GET /api/trust): Booking.com score + count (read daily, unedited), Google rating, reply time, legal identity.
import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ExternalLink, ShieldCheck } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { sectionPath } from '../siteRoutes';

let cached = null;

export const useTrust = () => {
  const [data, setData] = useState(cached);
  useEffect(() => {
    if (cached) return;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/trust`).then((r) => (r.ok ? r.json() : null)).then((d) => { cached = d; setData(d); }).catch(() => {});
  }, []);
  return data;
};

const T = {
  en: { on: 'on Booking.com', reviews: 'reviews', google: 'on Google', unedited: 'Scores and reviews are copied from Booking.com and Google unedited — including the critical ones.', reply: 'Typical reply to messages: {h} h', verified: 'verified guest reviews', updated: 'updated' , labels: { Good: 'Good', 'Very Good': 'Very Good', Pleasant: 'Pleasant', Fabulous: 'Fabulous' } },
  me: { on: 'na Booking.com', reviews: 'recenzija', google: 'na Google-u', unedited: 'Ocjene i recenzije preuzete su sa Booking.com-a i Google-a bez izmjena — uključujući i kritične.', reply: 'Uobičajen odgovor na poruke: {h} h', verified: 'provjerenih recenzija gostiju', updated: 'ažurirano', labels: { Good: 'Dobro', 'Very Good': 'Vrlo dobro', Pleasant: 'Prijatno', Fabulous: 'Odlično' } },
  sq: { on: 'në Booking.com', reviews: 'vlerësime', google: 'në Google', unedited: 'Pikët dhe vlerësimet janë kopjuar nga Booking.com dhe Google pa ndryshime — përfshirë ato kritike.', reply: 'Përgjigje tipike për mesazhet: {h} orë', verified: 'vlerësime të verifikuara', updated: 'përditësuar', labels: { Good: 'Mirë', 'Very Good': 'Shumë mirë', Pleasant: 'Këndshëm', Fabulous: 'Shkëlqyer' } },
  de: { on: 'auf Booking.com', reviews: 'Bewertungen', google: 'bei Google', unedited: 'Noten und Bewertungen werden unverändert von Booking.com und Google übernommen – auch die kritischen.', reply: 'Übliche Antwortzeit auf Nachrichten: {h} Std.', verified: 'verifizierte Gästebewertungen', updated: 'aktualisiert', labels: { Good: 'Gut', 'Very Good': 'Sehr gut', Pleasant: 'Angenehm', Fabulous: 'Fabelhaft' } },
  ru: { on: 'на Booking.com', reviews: 'отзывов', google: 'в Google', unedited: 'Оценки и отзывы скопированы с Booking.com и Google без изменений — включая критические.', reply: 'Обычно отвечаем на сообщения за {h} ч', verified: 'подтверждённых отзывов гостей', updated: 'обновлено', labels: { Good: 'Хорошо', 'Very Good': 'Очень хорошо', Pleasant: 'Приятно', Fabulous: 'Превосходно' } },
  pl: { on: 'na Booking.com', reviews: 'opinii', google: 'w Google', unedited: 'Oceny i opinie są kopiowane z Booking.com i Google bez zmian — także te krytyczne.', reply: 'Zwykły czas odpowiedzi na wiadomości: {h} h', verified: 'zweryfikowanych opinii gości', updated: 'aktualizacja', labels: { Good: 'Dobry', 'Very Good': 'Bardzo dobry', Pleasant: 'Przyjemny', Fabulous: 'Znakomity' } },
  hu: { on: 'a Booking.com-on', reviews: 'értékelés', google: 'a Google-on', unedited: 'A pontszámokat és értékeléseket változtatás nélkül vesszük át a Booking.com-ról és a Google-ról — a kritikusakat is.', reply: 'Jellemző válaszidő üzenetekre: {h} óra', verified: 'ellenőrzött vendégértékelés', updated: 'frissítve', labels: { Good: 'Jó', 'Very Good': 'Nagyon jó', Pleasant: 'Kellemes', Fabulous: 'Kiváló' } },
};

export const scoreLabel = (s) => (s >= 9 ? 'Fabulous' : s >= 8 ? 'Very Good' : s >= 7 ? 'Good' : 'Pleasant');
export const scoreLabelText = (lang, s) => ((T[lang] || T.en).labels[scoreLabel(s)] || scoreLabel(s));

const STAMP = {
  en: 'Checked against Booking.com {d}', me: 'Provjereno sa Booking.com {d}', sq: 'Kontrolluar me Booking.com {d}', de: 'Geprüft gegen Booking.com {d}',
  ru: 'Сверено с Booking.com {d}', pl: 'Sprawdzone z Booking.com {d}', hu: 'Egyeztetve a Booking.com-mal {d}',
};
const TODAY = { en: 'this morning', me: 'jutros', sq: 'sot në mëngjes', de: 'heute Morgen', ru: 'сегодня утром', pl: 'dziś rano', hu: 'ma reggel' };

const stampDate = (lang, iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (d.toDateString() === new Date().toDateString()) return TODAY[lang] || TODAY.en;
  return d.toLocaleDateString(lang === 'me' ? 'sr-Latn' : lang, { day: 'numeric', month: 'short' });
};

// Small stamp next to the hero score: when the number was last verified against the source → /trust.
export const TrustStamp = ({ dark = true }) => {
  const { lang } = useLanguage();
  const { pathname } = useLocation();
  const data = useTrust();
  const iso = data?.booking?.fetched_at;
  if (!iso) return null;
  return (
    <Link to={sectionPath('trust', pathname)} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-medium transition-colors ${dark ? 'bg-white/10 text-white/85 hover:bg-white/20' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-testid="trust-stamp">
      <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
      <span>{(STAMP[lang] || STAMP.en).replace('{d}', stampDate(lang, iso))}</span>
      <span aria-hidden="true">→</span>
    </Link>
  );
};

// Hero / reviews badge: real Booking.com score with a link to the source.
export const TrustBadge = ({ dark = true }) => {
  const { lang } = useLanguage();
  const t = T[lang] || T.en;
  const data = useTrust();
  const b = data?.booking;
  if (!b?.score) return null;
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="trust-badge">
      <a href={b.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#003580] text-white px-3 py-2 rounded-lg hover:brightness-110 transition" data-testid="trust-badge-booking">
        <span className="font-bold text-lg leading-none">{b.score.toFixed(1)}</span>
        <span className="text-xs leading-tight">{t.labels[scoreLabel(b.score)] || scoreLabel(b.score)}<br />{b.count} {t.reviews} {t.on}</span>
        <ExternalLink className="w-3.5 h-3.5 opacity-70" />
      </a>
      {data?.google?.rating && (
        <a href={data.google.maps_url || '#'} target="_blank" rel="noopener noreferrer" className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg transition ${dark ? 'bg-white/15 text-white hover:bg-white/25' : 'bg-gray-100 text-gray-900 hover:bg-gray-200'}`} data-testid="trust-badge-google">
          <span className="font-bold text-lg leading-none">{Number(data.google.rating).toFixed(1)}</span>
          <span className="text-xs leading-tight">★ {data.google.count} {t.reviews}<br />{t.google}</span>
        </a>
      )}
      <TrustStamp dark={dark} />
    </div>
  );
};

// Footer legal identity line (registered name, address, TIN when set) + payment facts.
const LEGAL = {
  en: { tin: 'TIN', pay: 'Payment on site: cash or card (Visa, Mastercard, Maestro) · online deposits via Stripe (SSL encrypted) · prices include VAT', photos: 'Photos: our own and from our Booking.com listing, not retouched.' },
  me: { tin: 'PIB', pay: 'Plaćanje u hotelu: gotovina ili kartica (Visa, Mastercard, Maestro) · online depoziti preko Stripe-a (SSL) · cijene uključuju PDV', photos: 'Fotografije: naše i sa našeg Booking.com profila, bez retuširanja.' },
  sq: { tin: 'NIPT', pay: 'Pagesa në hotel: para në dorë ose kartë (Visa, Mastercard, Maestro) · depozita online përmes Stripe (SSL) · çmimet përfshijnë TVSH-në', photos: 'Fotot: tonat dhe nga profili ynë në Booking.com, pa retushim.' },
  de: { tin: 'Steuernr.', pay: 'Zahlung vor Ort: bar oder Karte (Visa, Mastercard, Maestro) · Online-Anzahlungen über Stripe (SSL) · Preise inkl. MwSt.', photos: 'Fotos: eigene und aus unserem Booking.com-Eintrag, nicht retuschiert.' },
  ru: { tin: 'ИНН', pay: 'Оплата в отеле: наличными или картой (Visa, Mastercard, Maestro) · онлайн-депозиты через Stripe (SSL) · цены с НДС', photos: 'Фото: наши и из нашего профиля на Booking.com, без ретуши.' },
  pl: { tin: 'NIP', pay: 'Płatność w hotelu: gotówka lub karta (Visa, Mastercard, Maestro) · zaliczki online przez Stripe (SSL) · ceny zawierają VAT', photos: 'Zdjęcia: nasze i z naszego profilu na Booking.com, bez retuszu.' },
  hu: { tin: 'Adószám', pay: 'Fizetés a helyszínen: készpénz vagy kártya (Visa, Mastercard, Maestro) · online előleg Stripe-on (SSL) · az árak tartalmazzák az áfát', photos: 'Fotók: sajátjaink és a Booking.com-profilunkról, retusálás nélkül.' },
};

export const TrustLegal = () => {
  const { lang } = useLanguage();
  const l = LEGAL[lang] || LEGAL.en;
  const data = useTrust();
  const legal = data?.legal;
  return (
    <div className="text-xs text-gray-400 space-y-1" data-testid="trust-legal">
      <p>{legal?.name || 'Hotel La Rosa Negra'} · {legal?.address || 'Velika Plaža, Kepi i Gjeranave, Ulcinj, Montenegro'}{legal?.tin ? ` · ${l.tin} ${legal.tin}` : ''}</p>
      <p>{l.pay}</p>
    </div>
  );
};

export const PhotoNote = ({ className = '' }) => {
  const { lang } = useLanguage();
  const l = LEGAL[lang] || LEGAL.en;
  return <p className={`text-xs text-gray-500 ${className}`} data-testid="photo-note">{l.photos}</p>;
};
export const TRUST_LABEL = { en: 'How we earn your trust', me: 'Kako gradimo povjerenje', sq: 'Si e fitojmë besimin', de: 'Wie wir Vertrauen verdienen', ru: 'Как мы зарабатываем доверие', pl: 'Jak zdobywamy zaufanie', hu: 'Hogyan érdemeljük ki a bizalmat' };

export const TrustNote = ({ className = '' }) => {
  const { lang } = useLanguage();
  const { pathname } = useLocation();
  const t = T[lang] || T.en;
  const data = useTrust();
  if (!data) return null;
  return (
    <p className={`text-xs flex flex-wrap items-center gap-x-3 gap-y-1 ${className}`} data-testid="trust-note">
      <span className="inline-flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" /> {t.unedited}</span>
      {data.verified_reviews > 0 && <span>· {data.verified_reviews} {t.verified}</span>}
      {data.reply_hours != null && <span>· {t.reply.replace('{h}', data.reply_hours < 1 ? '< 1' : data.reply_hours)}</span>}
      {data.booking?.fetched_at && <span>· {t.updated} {data.booking.fetched_at.slice(0, 10)}</span>}
      <Link to={sectionPath('trust', pathname)} className="underline underline-offset-2 hover:text-orange-500" data-testid="trust-note-link">{TRUST_LABEL[lang] || TRUST_LABEL.en} →</Link>
    </p>
  );
};
